import React, { useEffect, useState } from 'react';
import './FlashMessage.css';
import { DEFAULT_DURATION } from './FlashMessageContext';

/**
 * FlashMessage component for displaying success/error/info messages
 * 
 * @param {Object} props
 * @param {string} props.message - The message to display
 * @param {string} props.type - The type of message: 'success', 'error', or 'info'
 * @param {number} props.duration - Duration in ms before the message disappears (default: DEFAULT_DURATION)
 * @param {function} props.onClose - Callback function when message is closed
 */
const FlashMessage = ({ message, type = 'info', duration = DEFAULT_DURATION, onClose }) => {
  const [visible, setVisible] = useState(true);
  const [exitAnimation, setExitAnimation] = useState(false);

  // Icon mapping based on message type
  const icons = {
    success: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
    ),
    error: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
    ),
    info: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12" y2="8"></line>
      </svg>
    ),
  };

  // Close the message
  const closeMessage = () => {
    setExitAnimation(true);
    
    // Wait for exit animation to complete before calling onClose
    setTimeout(() => {
      setVisible(false);
      if (onClose) onClose();
    }, 300); // Duration of exit animation
  };

  // Auto-close after duration
  useEffect(() => {
    if (!duration) return;
    
    const timer = setTimeout(() => {
      closeMessage();
    }, duration);
    
    return () => clearTimeout(timer);
  }, [duration]);

  if (!visible) return null;

  return (
    <div 
      className={`flash-message flash-message-${type} ${exitAnimation ? 'flash-message-exit' : ''}`}
      role="alert"
    >
      <div className="flash-message-icon">
        {icons[type]}
      </div>
      <div className="flash-message-content">
        <p>{message}</p>
      </div>
      <button 
        className="flash-message-close" 
        onClick={closeMessage}
        aria-label="Close message"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
  );
};

export default FlashMessage; 